import { ComponentProps, FunctionComponent } from "react";
import {
  ClinicalIcon,
  DemographicsIcon,
  GenomicsIcon,
  BiospecimenIcon,
  PlusMinusIcon,
  GermlineIcon,
  PillIcon,
  CancerIcon,
  SequencingIcon,
  TissueSampleIcon,
  DefaultDomainIcon,
} from "~/assets/icons";

export enum DomainIconOptions {
  PERSON = "PERSON",
  CELL = "CELL",
  PLUS_MINUS = "PLUS_MINUS",
  TEST_TUBE = "TEST_TUBE",
  MICROSCOPIC = "MICROSCOPIC",
  SEQUENCE = "SEQUENCE",
  DNA = "DNA",
  DNA_DOWN = "DNA_DOWN",
  DEFAULT = "DEFAULT",
}

export interface DomainIconDisplay {
  icon: FunctionComponent<ComponentProps<"svg">>;
  iconColor: string;
  backgroundColor: string;
}

export const DISPLAY_DOMAIN_ICON: Record<DomainIconOptions, DomainIconDisplay> = {
  [DomainIconOptions.PERSON]: {
    icon: DemographicsIcon,
    iconColor: "#4185F3",
    backgroundColor: "#E4EDFA",
  },
  [DomainIconOptions.CELL]: {
    icon: CancerIcon,
    iconColor: "#DD37F8",
    backgroundColor: "#FCE6FF",
  },
  [DomainIconOptions.PLUS_MINUS]: {
    icon: PlusMinusIcon,
    iconColor: "#DD37F8",
    backgroundColor: "#FCE6FF",
  },
  [DomainIconOptions.TEST_TUBE]: {
    icon: BiospecimenIcon,
    iconColor: "#FF4C1A",
    backgroundColor: "#FFEBE5",
  },
  [DomainIconOptions.MICROSCOPIC]: {
    icon: TissueSampleIcon,
    iconColor: "#FF4C1A",
    backgroundColor: "#FFEBE5",
  },
  [DomainIconOptions.SEQUENCE]: {
    icon: SequencingIcon,
    iconColor: "#01BD79",
    backgroundColor: "#E0FCF3",
  },
  [DomainIconOptions.DNA]: {
    icon: GenomicsIcon,
    iconColor: "#01BD79",
    backgroundColor: "#E0FCF3",
  },
  [DomainIconOptions.DNA_DOWN]: {
    icon: GermlineIcon,
    iconColor: "#01BD79",
    backgroundColor: "#E0FCF3",
  },
  [DomainIconOptions.DEFAULT]: {
    icon: DefaultDomainIcon,
    iconColor: "#860DFF",
    backgroundColor: "#F2E6FF",
  },
};

export const enum Domain {
  DEMOGRAPHICS = "demographics",
  DRUG_EXPOSURE = "drug_exposure",
  CANCER_DIAGNOSIS = "cancer_diagnosis",
  TUMOR_BIOMARKER = "tumor_biomarker",
  TISSUE_BIOSPECIMEN = "tissue_biospecimen",
  BLOOD_BIOSPECIMEN = "blood_biospecimen",
  SOMATIC_SNV = "somatic_snv",
  GERMLINE_SNV = "germline_snv",
  SEQUENCING_OCCURRENCE = "sequencing_occurrence",
  DIAGNOSIS = "diagnosis",
  ENCOUNTER = "encounter",
  LABS = "labs",
  BIOSPECIMEN = "biospecimen",
}

export interface DomainDisplay extends DomainIconDisplay {
  label: string;
}

export const DISPLAY_DOMAIN: Record<Domain, DomainDisplay> = {
  [Domain.DEMOGRAPHICS]: {
    label: "Demographics",
    icon: DemographicsIcon,
    iconColor: "#4185F3",
    backgroundColor: "#E4EDFA",
  },
  [Domain.CANCER_DIAGNOSIS]: {
    label: "Cancer Diagnosis",
    icon: CancerIcon,
    iconColor: "#DD37F8",
    backgroundColor: "#FCE6FF",
  },
  [Domain.TUMOR_BIOMARKER]: {
    label: "Tumor Biomarker",
    icon: PlusMinusIcon,
    iconColor: "#DD37F8",
    backgroundColor: "#FCE6FF",
  },
  [Domain.TISSUE_BIOSPECIMEN]: {
    label: "Tissue Availability",
    icon: TissueSampleIcon,
    iconColor: "#FF4C1A",
    backgroundColor: "#FFEBE5",
  },
  [Domain.BLOOD_BIOSPECIMEN]: {
    label: "Blood Availability",
    icon: BiospecimenIcon,
    iconColor: "#FF4C1A",
    backgroundColor: "#FFEBE5",
  },
  [Domain.DRUG_EXPOSURE]: {
    label: "Drug Exposures",
    icon: PillIcon,
    iconColor: "#DD37F8",
    backgroundColor: "#FCE6FF",
  },
  [Domain.SOMATIC_SNV]: {
    label: "Somatic Variants",
    icon: GenomicsIcon,
    iconColor: "#01BD79",
    backgroundColor: "#E0FCF3",
  },
  [Domain.GERMLINE_SNV]: {
    label: "Germline Variants",
    icon: GermlineIcon,
    iconColor: "#01BD79",
    backgroundColor: "#E0FCF3",
  },
  [Domain.SEQUENCING_OCCURRENCE]: {
    label: "Sequencing Availability",
    icon: SequencingIcon,
    iconColor: "#01BD79",
    backgroundColor: "#E0FCF3",
  },
  [Domain.DIAGNOSIS]: {
    label: "Diagnosis",
    icon: CancerIcon,
    iconColor: "#DD37F8",
    backgroundColor: "#FCE6FF",
  },
  [Domain.ENCOUNTER]: {
    label: "Encounter",
    icon: SequencingIcon,
    iconColor: "#01BD79",
    backgroundColor: "#E0FCF3",
  },
  [Domain.LABS]: {
    label: "Labs",
    icon: ClinicalIcon,
    iconColor: "#01BD79",
    backgroundColor: "#E0FCF3",
  },
  [Domain.BIOSPECIMEN]: {
    label: "Biospecimen Availability",
    icon: BiospecimenIcon,
    iconColor: "#FF4C1A",
    backgroundColor: "#FFEBE5",
  },
};

export default Domain;
